import {CheckboxOption, FormControl, FormField, Separator} from 'platform/components';
import {Heading, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isNotEmpty, not} from 'ramda';

import {UpdateScopeSupplierArticle} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {isNotNilOrEmpty, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {OverviewForm} from '../types/OverviewForm';

interface SupplierArticleUpdateScopeProps extends RequiredTestIdProps {
  control: FormControl<OverviewForm>;
  formApi: UseFormReturn<OverviewForm>;
}

export function SupplierArticleUpdateScope(props: SupplierArticleUpdateScopeProps) {
  const [shouldUpdateAll, currentScope] = useWatch({
    control: props.control,
    name: ['updateScope.updateScopeSupplierArticleAll', 'updateScope.updateScopeSupplierArticle'],
  });

  const handleUpdateAllChange = (value: boolean) => {
    if (value) {
      props.formApi.setValue(
        'updateScope.updateScopeSupplierArticle',
        options.map((option) => option.value as UpdateScopeSupplierArticle)
      );
      return;
    }
    props.formApi.setValue('updateScope.updateScopeSupplierArticle', []);
  };

  const handleScopeChange = (value: string[] | null) => {
    const hasAllOptionsSelected =
      isNotNilOrEmpty(value) && options.every((option) => value?.includes(option.value));

    if (hasAllOptionsSelected) {
      props.formApi.setValue('updateScope.updateScopeSupplierArticleAll', hasAllOptionsSelected);
      return;
    }

    props.formApi.setValue('updateScope.updateScopeSupplierArticleAll', hasAllOptionsSelected);
  };

  const hasSomeOptionsSelected = not(shouldUpdateAll) && isNotEmpty(currentScope);

  return (
    <VStack spacing={4}>
      <Heading size={5}>{i18n.t('entity.warehouse.labels.supplierArticle')}</Heading>
      <FormField
        isDisabled
        isIndeterminate={hasSomeOptionsSelected}
        control={props.control}
        type="checkbox"
        name="updateScope.updateScopeSupplierArticleAll"
        label={i18n.t('entity.warehouse.labels.updateAll')}
        onChange={handleUpdateAllChange}
        data-testid={suffixTestId('inputs.updateAll', props)}
      />
      <Separator spacing={0} />
      <FormField
        control={props.control}
        type="checkboxes"
        name="updateScope.updateScopeSupplierArticle"
        options={options}
        onChange={handleScopeChange}
        direction="vertical"
        data-testid={suffixTestId('inputs.supplierArticleScope', props)}
      />
    </VStack>
  );
}

const options: CheckboxOption[] = [
  {
    label: i18n.t('entity.warehouse.labels.recommendedPrice'),
    value: 'RECOMMENDED_PRICE',
    isDisabled: true,
  },
  {
    label: i18n.t('entity.warehouse.labels.purchasePrice'),
    value: 'PURCHASE_PRICE',
    isDisabled: true,
  },
  {
    label: i18n.t('entity.warehouse.labels.discountGroup'),
    value: 'SUPPLIER_DISCOUNT_GROUP',
    isDisabled: true,
  },
  {
    label: i18n.t('entity.warehouse.labels.bulkPkgQty'),
    value: 'BULK_PACKAGE_QUANTITY',
    isDisabled: true,
  },
  {
    label: i18n.t('entity.warehouse.labels.orderingNumber'),
    value: 'ORDERING_NUMBER',
    isDisabled: true,
  },
];
